import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  metricCatalogService,
  AttributeTypeMapping,
  AttributeType,
  MeasurementDomain,
  Metric,
  MetricVariant,
  ValueType,
  Unit,
} from '@/services/metricCatalogService';

export function MappingsTab() {
  const [search, setSearch] = useState('');
  const [unmappedOnly, setUnmappedOnly] = useState(false);
  const [editingMapping, setEditingMapping] = useState<AttributeTypeMapping | null>(null);
  const [editingAttributeType, setEditingAttributeType] = useState<AttributeType | null>(null);
  const queryClient = useQueryClient();

  // Form state
  const [formDomainId, setFormDomainId] = useState<number | ''>('');
  const [formMetricId, setFormMetricId] = useState<number | ''>('');
  const [formVariantId, setFormVariantId] = useState<number | '' | null>('');
  const [formValueTypeId, setFormValueTypeId] = useState<number | ''>('');
  const [formUnitId, setFormUnitId] = useState<number | '' | null>('');

  const { data: attributeTypes = [] } = useQuery({
    queryKey: ['metricCatalog', 'attributeTypes', search],
    queryFn: () => metricCatalogService.getAttributeTypes(search),
  });

  const { data: mappings = [] } = useQuery({
    queryKey: ['metricCatalog', 'mappings'],
    queryFn: () => metricCatalogService.getMappings(),
  });

  const { data: domains = [] } = useQuery({
    queryKey: ['metricCatalog', 'domains'],
    queryFn: () => metricCatalogService.getDomains(),
  });

  const { data: valueTypes = [] } = useQuery({
    queryKey: ['metricCatalog', 'valueTypes'],
    queryFn: () => metricCatalogService.getValueTypes(),
  });

  const { data: units = [] } = useQuery({
    queryKey: ['metricCatalog', 'units'],
    queryFn: () => metricCatalogService.getUnits(),
  });

  const { data: metrics = [] } = useQuery({
    queryKey: ['metricCatalog', 'metrics', formDomainId],
    queryFn: () => metricCatalogService.getMetrics(formDomainId ? Number(formDomainId) : undefined),
    enabled: !!formDomainId,
  });

  const { data: variants = [] } = useQuery({
    queryKey: ['metricCatalog', 'variants', formMetricId],
    queryFn: () => metricCatalogService.getVariants(formMetricId ? Number(formMetricId) : undefined),
    enabled: !!formMetricId,
  });

  const createMutation = useMutation({
    mutationFn: ({
      attributeTypeId,
      domainId,
      metricId,
      variantId,
      valueTypeId,
      unitId,
    }: {
      attributeTypeId: number;
      domainId: number;
      metricId: number;
      variantId: number | null;
      valueTypeId: number;
      unitId: number | null;
    }) => metricCatalogService.createMapping(attributeTypeId, domainId, metricId, variantId, valueTypeId, unitId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['metricCatalog', 'mappings'] });
      setEditingMapping(null);
      setEditingAttributeType(null);
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      attributeTypeId,
      domainId,
      metricId,
      variantId,
      valueTypeId,
      unitId,
    }: {
      id: number;
      attributeTypeId: number;
      domainId: number;
      metricId: number;
      variantId: number | null;
      valueTypeId: number;
      unitId: number | null;
    }) => metricCatalogService.updateMapping(id, attributeTypeId, domainId, metricId, variantId, valueTypeId, unitId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['metricCatalog', 'mappings'] });
      setEditingMapping(null);
      setEditingAttributeType(null);
      resetForm();
    },
  });

  const resetForm = () => {
    setFormDomainId('');
    setFormMetricId('');
    setFormVariantId('');
    setFormValueTypeId('');
    setFormUnitId('');
  };

  const handleEdit = (mapping: AttributeTypeMapping) => {
    setEditingMapping(mapping);
    setFormDomainId(mapping.measurementDomain.id);
    setFormMetricId(mapping.metric.id);
    setFormVariantId(mapping.variant?.id || null);
    setFormValueTypeId(mapping.valueType.id);
    setFormUnitId(mapping.unit?.id || null);
  };

  const handleCreateNew = (attributeType: AttributeType) => {
    setEditingMapping(null);
    setEditingAttributeType(attributeType);
    resetForm();
  };

  const handleSave = () => {
    if (!formDomainId || !formMetricId || !formValueTypeId) return;

    const domainId = Number(formDomainId);
    const metricId = Number(formMetricId);
    const variantId = formVariantId ? Number(formVariantId) : null;
    const valueTypeId = Number(formValueTypeId);
    const unitId = formUnitId ? Number(formUnitId) : null;

    if (editingMapping) {
      updateMutation.mutate({
        id: editingMapping.id,
        attributeTypeId: editingMapping.attributeType.id,
        domainId,
        metricId,
        variantId,
        valueTypeId,
        unitId,
      });
    } else if (editingAttributeType) {
      createMutation.mutate({
        attributeTypeId: editingAttributeType.id,
        domainId,
        metricId,
        variantId,
        valueTypeId,
        unitId,
      });
    }
  };

  // Reset dependent selects when parent changes
  useEffect(() => {
    if (!formDomainId) {
      setFormMetricId('');
      setFormVariantId('');
    }
  }, [formDomainId]);

  useEffect(() => {
    if (!formMetricId) {
      setFormVariantId('');
    }
  }, [formMetricId]);

  const filteredAttributeTypes = useMemo(() => {
    const filtered = attributeTypes.filter((at) => {
      if (unmappedOnly) {
        return !mappings.some((m) => m.attributeType.id === at.id);
      }
      return true;
    });

    // Unmapped first, then by code (stable UX)
    return filtered.sort((a, b) => {
      const aHas = mappings.some((m) => m.attributeType.id === a.id);
      const bHas = mappings.some((m) => m.attributeType.id === b.id);
      if (aHas !== bHas) return aHas ? 1 : -1;
      return a.code.localeCompare(b.code);
    });
  }, [attributeTypes, mappings, unmappedOnly]);

  const getMappingForAttributeType = (attributeTypeId: number): AttributeTypeMapping | undefined => {
    return mappings.find((m) => m.attributeType.id === attributeTypeId);
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Attribute Type Mappings</h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Associer les AttributeType aux domaines/métriques/variantes du catalogue
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
            <input
              type="checkbox"
              checked={unmappedOnly}
              onChange={(e) => setUnmappedOnly(e.target.checked)}
              className="rounded border-slate-300 dark:border-slate-600"
            />
            Non mappés uniquement
          </label>
          <input
            type="text"
            placeholder="Rechercher par code AttributeType..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-[#0B1116] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>

      <div className="bg-white dark:bg-[#16202A] border border-slate-200 dark:border-[#1E293B] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 dark:bg-[#0B1116] text-slate-500 dark:text-slate-400">
              <tr>
                <th className="text-left font-medium px-5 py-3">AttributeType</th>
                <th className="text-left font-medium px-5 py-3">Domaine</th>
                <th className="text-left font-medium px-5 py-3">Metric</th>
                <th className="text-left font-medium px-5 py-3">Variant</th>
                <th className="text-left font-medium px-5 py-3">ValueType</th>
                <th className="text-left font-medium px-5 py-3">Unit</th>
                <th className="text-right font-medium px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAttributeTypes.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-8 text-center text-slate-500 dark:text-slate-400">
                    Aucun AttributeType trouvé.
                  </td>
                </tr>
              ) : (
                filteredAttributeTypes.map((at) => {
                  const mapping = getMappingForAttributeType(at.id);
                  const isUnmapped = !mapping;
                  return (
                    <tr
                      key={at.id}
                      className={`border-t border-slate-100 dark:border-slate-800 ${
                        isUnmapped ? 'bg-red-50/60 dark:bg-red-950/20' : ''
                      }`}
                    >
                      <td className="px-5 py-4">
                        <div
                          className={`font-medium ${
                            isUnmapped ? 'text-red-700 dark:text-red-300' : 'text-slate-900 dark:text-white'
                          }`}
                        >
                          {at.code}
                        </div>
                        {at.name && <div className="text-xs text-slate-500 dark:text-slate-400">{at.name}</div>}
                      </td>
                      <td className="px-5 py-4 text-slate-700 dark:text-slate-300">
                        {mapping?.measurementDomain.code || '—'}
                      </td>
                      <td className="px-5 py-4 text-slate-700 dark:text-slate-300">{mapping?.metric.code || '—'}</td>
                      <td className="px-5 py-4 text-slate-700 dark:text-slate-300">{mapping?.variant?.code || '—'}</td>
                      <td className="px-5 py-4 text-slate-700 dark:text-slate-300">{mapping?.valueType.code || '—'}</td>
                      <td className="px-5 py-4 text-slate-700 dark:text-slate-300">{mapping?.unit?.code || '—'}</td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => (mapping ? handleEdit(mapping) : handleCreateNew(at))}
                            className="p-2 text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-accent hover:bg-slate-100 dark:hover:bg-gray-800 rounded transition-colors"
                            title={mapping ? 'Modifier mapping' : 'Créer mapping'}
                          >
                            <span className="material-icons-outlined text-lg">{mapping ? 'edit' : 'add'}</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit/Create Modal */}
      {(editingMapping || editingAttributeType) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-[#16202A] rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
              {editingMapping ? 'Modifier Mapping' : 'Créer Mapping'}
              {editingAttributeType && (
                <span className="ml-2 text-sm font-normal text-slate-500 dark:text-slate-400">
                  pour {editingAttributeType.code}
                </span>
              )}
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Domaine *
                  </label>
                  <select
                    value={formDomainId}
                    onChange={(e) => setFormDomainId(e.target.value ? Number(e.target.value) : '')}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-[#0B1116] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Sélectionner un domaine</option>
                    {domains.map((domain) => (
                      <option key={domain.id} value={domain.id}>
                        {domain.code}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Metric *
                  </label>
                  <select
                    value={formMetricId}
                    onChange={(e) => setFormMetricId(e.target.value ? Number(e.target.value) : '')}
                    disabled={!formDomainId}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-[#0B1116] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">Sélectionner une métrique</option>
                    {metrics.map((metric) => (
                      <option key={metric.id} value={metric.id}>
                        {metric.code}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Variant
                  </label>
                  <select
                    value={formVariantId || ''}
                    onChange={(e) => setFormVariantId(e.target.value ? Number(e.target.value) : null)}
                    disabled={!formMetricId}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-[#0B1116] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">Aucun</option>
                    {variants.map((variant) => (
                      <option key={variant.id} value={variant.id}>
                        {variant.code}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    ValueType *
                  </label>
                  <select
                    value={formValueTypeId}
                    onChange={(e) => setFormValueTypeId(e.target.value ? Number(e.target.value) : '')}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-[#0B1116] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Sélectionner un type</option>
                    {valueTypes.map((vt) => (
                      <option key={vt.id} value={vt.id}>
                        {vt.code}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Unit</label>
                  <select
                    value={formUnitId || ''}
                    onChange={(e) => setFormUnitId(e.target.value ? Number(e.target.value) : null)}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-[#0B1116] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Aucun</option>
                    {units.map((unit) => (
                      <option key={unit.id} value={unit.id}>
                        {unit.code}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-[#1E293B]">
                <button
                  onClick={() => {
                    setEditingMapping(null);
                    setEditingAttributeType(null);
                    resetForm();
                  }}
                  className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSave}
                  disabled={
                    !formDomainId ||
                    !formMetricId ||
                    !formValueTypeId ||
                    createMutation.isPending ||
                    updateMutation.isPending
                  }
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createMutation.isPending || updateMutation.isPending ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
